import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, ReferenceLine,
} from 'recharts';
import { RefreshCw, AlertTriangle, Activity, Users, Zap } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const ERROR_LABELS = {
  js_error:             'JS Error',
  api_error:            'API Error',
  unhandled_rejection:  'Unhandled Rejection',
};

const ERROR_COLORS = {
  js_error:             '#ef4444',
  api_error:            '#f97316',
  unhandled_rejection:  '#a855f7',
};

function KpiCard({ icon: Icon, label, value, highlight }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: '#fff', borderRadius: 10, padding: '20px 24px',
        border: highlight && value > 0 ? '1px solid #fca5a5' : '1px solid #e5e7eb',
        flex: 1, minWidth: 180,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8,
          background: highlight && value > 0 ? '#fee2e2' : '#f0fdf4',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={16} color={highlight && value > 0 ? '#dc2626' : '#16a34a'} />
        </div>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: highlight && value > 0 ? '#dc2626' : '#111827' }}>
        {(value ?? 0).toLocaleString()}
      </div>
      {highlight && value > 0 && (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#dc2626' }}>Requires attention</p>
      )}
    </div>
  );
}

function EventTypeBadge({ type }) {
  const label = ERROR_LABELS[type] || type;
  const color = ERROR_COLORS[type] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: color + '18', color,
    }}>{label}</span>
  );
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const PAGE_BAR_COLOR = '#194449';

const TIME_RANGES = [
  { label: '6h',  hours: 6 },
  { label: '24h', hours: 24 },
  { label: '3d',  days: 3 },
  { label: '7d',  days: 7 },
  { label: '10d', days: 10 },
];

export default function PortalAnalyticsPage() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [timeRange, setTimeRange]     = useState(TIME_RANGES[1]); // default 24h
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = timeRange.hours != null ? { hours: timeRange.hours } : { days: timeRange.days };
      const res = await axios.get(`${API}/api/admin/portal/analytics`, { params, withCredentials: true });
      console.log('Telemetry status:', res.status);
      console.log('Telemetry data:', res.data);
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Telemetry fetch failed:', err?.response?.status, err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const kpi = data?.kpi || {};
  const rangeLabel = data?.range_label || '24h';
  const topPages = (data?.top_pages || []).map(r => ({ name: r.page_slug, views: r.views }));
  const errorsMap = data?.errors_by_type || {};
  const recentErrors = data?.recent_errors || [];
  const errorTrend = data?.error_trend || [];
  const topIssues = data?.top_issues || [];

  // Peak value for sparkline annotation
  const peakCount = errorTrend.reduce((m, d) => Math.max(m, d.count), 0);

  return (
    <div style={{ padding: '28px 32px', fontFamily: '"DM Sans", sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f2a35', margin: 0 }}>Portal Analytics</h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
            App 3 usage — page views, visitors, and errors ({rangeLabel}).
            {lastRefresh && (
              <span style={{ marginLeft: 8, color: '#9ca3af' }}>
                Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Time Range Selector */}
          <div data-testid="time-range-selector" style={{ display: 'flex', gap: 3, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
            {TIME_RANGES.map(r => (
              <button
                key={r.label}
                data-testid={`time-range-${r.label}`}
                onClick={() => setTimeRange(r)}
                style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                  border: 'none', cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                  background: timeRange.label === r.label ? '#194449' : 'transparent',
                  color: timeRange.label === r.label ? '#fff' : '#6b7280',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="analytics-refresh-btn"
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }} data-testid="analytics-kpi-row">
        <KpiCard icon={Zap}           label="Total Events"    value={kpi.total_events}  />
        <KpiCard icon={Users}         label="Unique Visitors" value={kpi.unique_users}  />
        <KpiCard icon={AlertTriangle} label="Errors"          value={kpi.total_errors}  highlight />
      </div>

      {/* Row 1b: 24h Error Trend Sparkline */}
      <div
        data-testid="error-trend-panel"
        style={{
          background: '#fff', borderRadius: 10,
          border: kpi.total_errors > 0 ? '1px solid #fca5a5' : '1px solid #e5e7eb',
          padding: '16px 24px', marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Error Trend ({rangeLabel})</span>
            <span style={{ marginLeft: 10, fontSize: 11, color: '#9ca3af' }}>
              {timeRange.days ? 'daily' : 'hourly'} error count — {rangeLabel}
            </span>
          </div>
          {peakCount > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
              background: '#fee2e2', color: '#dc2626',
            }}>
              Peak: {peakCount} at {errorTrend.find(d => d.count === peakCount)?.label}
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={88}>
          <AreaChart data={errorTrend} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
            <defs>
              <linearGradient id="errGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor="#ef4444" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 4" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#d1d5db' }}
              axisLine={false} tickLine={false}
              interval={3}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: '#d1d5db' }}
              axisLine={false} tickLine={false}
              width={30}
            />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb', padding: '4px 10px' }}
              formatter={(v) => [v, 'Errors']}
              labelStyle={{ fontWeight: 700, color: '#374151' }}
            />
            {peakCount > 0 && (
              <ReferenceLine
                x={errorTrend.find(d => d.count === peakCount)?.label}
                stroke="#ef444480"
                strokeDasharray="3 3"
              />
            )}
            <Area
              type="monotone"
              dataKey="count"
              stroke="#ef4444"
              strokeWidth={2}
              fill="url(#errGrad)"
              dot={false}
              activeDot={{ r: 4, fill: '#ef4444', strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        {peakCount === 0 && (
          <p style={{ margin: '-4px 0 0', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
            No errors in the last 24 hours
          </p>
        )}
      </div>

      {/* Row 2: Top Pages Bar Chart */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '20px 24px', marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Top Pages ({rangeLabel})</h2>
        {topPages.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No page view data available.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(180, topPages.length * 38)}>
            <BarChart
              data={topPages}
              layout="vertical"
              margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis
                type="category" dataKey="name"
                tick={{ fontSize: 12, fill: '#374151' }}
                width={160} axisLine={false} tickLine={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                formatter={(v) => [v.toLocaleString(), 'Page Views']}
              />
              <Bar dataKey="views" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {topPages.map((_, idx) => (
                  <Cell key={idx} fill={PAGE_BAR_COLOR} opacity={1 - idx * 0.07} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Row 3: Errors by Type */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {['js_error', 'api_error', 'unhandled_rejection'].map(type => (
          <div
            key={type}
            data-testid={`error-bucket-${type}`}
            style={{
              flex: 1, background: '#fff', borderRadius: 10, padding: '16px 20px',
              border: `1px solid ${ERROR_COLORS[type]}30`,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: ERROR_COLORS[type], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {ERROR_LABELS[type]}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#111827' }}>
              {(errorsMap[type] || 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Row 4: Recent Errors Table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Recent Errors ({rangeLabel})</h2>
        </div>
        {recentErrors.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            No errors recorded in {rangeLabel}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Time', 'Type', 'Page', 'Message'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentErrors.map((err, i) => (
                  <tr
                    key={i}
                    data-testid={`error-row-${i}`}
                    style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                  >
                    <td style={{ padding: '8px 14px', whiteSpace: 'nowrap', color: '#374151' }}>{fmtTime(err.created_at)}</td>
                    <td style={{ padding: '8px 14px' }}><EventTypeBadge type={err.event_type} /></td>
                    <td style={{ padding: '8px 14px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>{err.page_slug || '—'}</td>
                    <td style={{ padding: '8px 14px', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }} title={err.error_message || ''}>
                      {err.error_message ? err.error_message.slice(0, 100) + (err.error_message.length > 100 ? '…' : '') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Row 5: Top Issues Table */}
      <div
        data-testid="top-issues-panel"
        style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden', marginTop: 20 }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Top Issues ({rangeLabel})</h2>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>sorted by affected users</span>
        </div>
        {topIssues.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            No issues recorded in {rangeLabel}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Error Message', 'Type', 'Affected Users', 'Occurrences', 'Last Seen'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topIssues.map((issue, i) => (
                  <tr
                    key={i}
                    data-testid={`top-issue-row-${i}`}
                    style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                  >
                    <td style={{ padding: '8px 14px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }} title={issue.error_message || ''}>
                      {issue.error_message ? issue.error_message.slice(0, 120) + (issue.error_message.length > 120 ? '…' : '') : '—'}
                    </td>
                    <td style={{ padding: '8px 14px' }}><EventTypeBadge type={issue.event_type} /></td>
                    <td style={{ padding: '8px 14px', fontWeight: 700, color: '#374151' }}>
                      {(issue.affected_users ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 14px', color: '#374151' }}>
                      {(issue.occurrences ?? 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 14px', whiteSpace: 'nowrap', color: '#374151' }}>
                      {fmtTime(issue.last_seen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
